import { useEffect, useRef } from 'react';

import { Gender, HairColor, MobileTab, WardrobeCategory } from '@/constants/enums';
import { getBackgroundPresetsByGender, getHairstylePresetsByGender, getPosePresetsByGender } from '@/constants/generated';
import { importWardrobeItemFromUrl } from '@/hooks/useWardrobeUpload';
import { getAppStore, useAppStore } from '@/store';
import type { IAppStore } from '@/store';
import type { ComposerTab } from '@/store/wardrobe/types';
import type { WardrobeItem } from '@/types';
import { readDeeplinkParams, stripDeeplinkParams } from '@/utils/deeplinkParams';
import { isValidRemoteUrl } from '@/utils/isValidRemoteUrl';
import { validateSubscription } from '@/utils/validateSubscription';

/**
 * Canonical (camelCase) deeplink query params supported by AI Stylist:
 *
 * - gender          female | male                       (applied first — resets other selections)
 * - imageUrl        http(s) URL of the character photo
 * - outfit          `complete-outfits` item id for the gender
 * - items           comma list of `category:itemId` (one per category)
 * - garmentUrl      http(s) URL of a custom garment, requires garmentCategory
 * - garmentCategory one of the wardrobe categories
 * - hairstyle       hairstyle preset id for the gender
 * - hairColor       HairColor name (case/space-insensitive, e.g. `ravenBlack`)
 * - pose            pose preset id for the gender
 * - background      background preset id for the gender
 * - tab             character | wardrobe
 *
 * A complete outfit is exclusive in the store (it cannot coexist with other
 * clothing), so a valid `outfit` (or a complete outfit listed in `items`) wins
 * over the other `items` and `garmentUrl`. Otherwise
 * `items` are applied first and the custom garment replaces its category. The
 * garment import runs the same auth + credits gate as the picker.
 *
 * Params are read once, applied to the store, and stripped from the location
 * store. Invalid / unknown values are ignored. Nothing is auto-generated.
 */
const DEEPLINK_PARAM_KEYS = ['gender', 'imageUrl', 'outfit', 'items', 'garmentUrl', 'garmentCategory', 'hairstyle', 'hairColor', 'pose', 'background', 'tab'] as const;

type DeeplinkParams = Partial<Record<typeof DEEPLINK_PARAM_KEYS[number], string>>;

const COMPOSER_TABS: readonly ComposerTab[] = ['character', 'wardrobe'];

const isOneOf =
  <T extends string>(values: readonly T[]) =>
  (value: string | undefined): value is T =>
    value !== undefined && (values as readonly string[]).includes(value);

const isGender = isOneOf(Object.values(Gender));
const isWardrobeCategory = isOneOf(Object.values(WardrobeCategory));
const isComposerTab = isOneOf(COMPOSER_TABS);

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const resolveHairColor = (value: string): HairColor | undefined => {
  const wanted = normalize(value);
  return Object.values(HairColor).find(color => normalize(color) === wanted);
};

/** Looks an item up in the store's current categories (already rebuilt for the active gender). */
const findWardrobeItem = (category: WardrobeCategory, itemId: string): WardrobeItem | undefined =>
  getAppStore()
    .wardrobe.categories.find(cat => cat.id === category)
    ?.items.find(item => item.id === itemId);

const parseItemRefs = (items: string): Array<{ category: WardrobeCategory; itemId: string }> =>
  items.split(',').flatMap(entry => {
    const separator = entry.indexOf(':');
    const category = entry.slice(0, separator).trim();
    const itemId = entry.slice(separator + 1).trim();
    return separator > 0 && itemId && isWardrobeCategory(category) ? [{ category, itemId }] : [];
  });

const applyGender = (store: IAppStore, { gender }: DeeplinkParams): Gender => {
  if (isGender(gender)) store.setGender(gender);
  return getAppStore().character.gender;
};

const applyCharacterImage = (store: IAppStore, { imageUrl }: DeeplinkParams): void => {
  if (isValidRemoteUrl(imageUrl)) store.setCharacterImage(imageUrl, null);
};

const applyWardrobe = async (store: IAppStore, { outfit, items, garmentUrl, garmentCategory }: DeeplinkParams): Promise<void> => {
  const itemRefs = items ? parseItemRefs(items) : [];
  // A complete outfit is exclusive: the first VALID one among `outfit` and the `items` refs wins over everything else.
  const outfitIds = [outfit, ...itemRefs.filter(ref => ref.category === WardrobeCategory.CompleteOutfits).map(ref => ref.itemId)].filter((id): id is string => !!id);
  const outfitItem = outfitIds.map(id => findWardrobeItem(WardrobeCategory.CompleteOutfits, id)).find(Boolean);
  if (outfitItem) {
    store.selectWardrobeItem(outfitItem);
    return;
  }

  itemRefs
    .filter(ref => ref.category !== WardrobeCategory.CompleteOutfits)
    .forEach(({ category, itemId }) => {
      const item = findWardrobeItem(category, itemId);
      if (item) store.selectWardrobeItem(item);
    });

  // Same auth + credits gate as the garment picker: the import runs thumbnail processing.
  if (isValidRemoteUrl(garmentUrl) && isWardrobeCategory(garmentCategory) && (await validateSubscription())) {
    store.selectWardrobeItem(importWardrobeItemFromUrl(garmentUrl, garmentCategory));
  }
};

const applyPresets = (store: IAppStore, { hairstyle, hairColor, pose, background }: DeeplinkParams, gender: Gender): void => {
  const hairstylePreset = hairstyle && getHairstylePresetsByGender(gender).find(p => p.id === hairstyle);
  if (hairstylePreset) store.setHairstyle(hairstylePreset);

  const color = hairColor && resolveHairColor(hairColor);
  if (color) store.setHairColor(color);

  const posePreset = pose && getPosePresetsByGender(gender).find(p => p.id === pose);
  if (posePreset) store.setPose(posePreset);

  const backgroundPreset = background && getBackgroundPresetsByGender(gender).find(p => p.id === background);
  if (backgroundPreset) store.setBackground(backgroundPreset);
};

const applyTab = (store: IAppStore, { tab }: DeeplinkParams): void => {
  if (!isComposerTab(tab)) return;
  store.setComposerTab(tab);
  store.setMobileTab(tab === 'wardrobe' ? MobileTab.Wardrobe : MobileTab.Character);
};

const applyDeeplinkParams = async (params: DeeplinkParams): Promise<void> => {
  const store = getAppStore();
  const gender = applyGender(store, params);
  applyCharacterImage(store, params);
  applyPresets(store, params, gender);
  applyTab(store, params);
  await applyWardrobe(store, params);
};

interface UseDeeplinkParamsOptions {
  isAuthorized: boolean;
  isAuthLoading: boolean;
}

const selectLibraryLoaded = (s: IAppStore) => s.library.libraryLoaded;
const selectWardrobeItemsLoaded = (s: IAppStore) => s.library.wardrobeItemsLoaded;

/**
 * Seeds the store from deeplink query params once the app is open and, for
 * signed-in users, once both Drive loads have settled: the character library
 * (otherwise it would race the seeded character/library entry) and the user's
 * saved wardrobe items (otherwise `items`/`outfit` referencing a saved user
 * item would be looked up before it exists in the categories and dropped).
 */
export function useDeeplinkParams({ isAuthorized, isAuthLoading }: UseDeeplinkParamsOptions): void {
  const handledRef = useRef(false);
  const libraryLoaded = useAppStore(selectLibraryLoaded);
  const wardrobeItemsLoaded = useAppStore(selectWardrobeItemsLoaded);

  useEffect(() => {
    if (handledRef.current || isAuthLoading) return;
    if (isAuthorized && !(libraryLoaded && wardrobeItemsLoaded)) return;
    handledRef.current = true;

    const params = readDeeplinkParams(DEEPLINK_PARAM_KEYS);
    stripDeeplinkParams(DEEPLINK_PARAM_KEYS);
    applyDeeplinkParams(params).catch((err: unknown) => {
      console.warn('[useDeeplinkParams] Failed to apply params:', err);
    });
  }, [isAuthorized, isAuthLoading, libraryLoaded, wardrobeItemsLoaded]);
}
